import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

function fmtNum(n) {
  if (n == null) return "—";
  return Number(n).toLocaleString("uk-UA");
}
function fmtPct(curr, prev) {
  if (!prev) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

function KPI({ label, value, delta, color, t, dark }) {
  const arrow = delta == null ? "" : delta >= 0 ? "▲" : "▼";
  const dColor = delta == null ? t.textMuted : delta >= 0 ? "#10b981" : "#ef4444";
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: color || t.text, marginTop: 6, fontFamily: "JetBrains Mono, monospace" }}>{fmtNum(value)}</div>
      {delta != null && (
        <div style={{ fontSize: 11, color: dColor, fontWeight: 600, marginTop: 4 }}>
          {arrow} {Math.abs(delta)}% vs пред. період
        </div>
      )}
    </div>
  );
}

export default function AnalyticsView() {
  const { proj, activeId, apiFetch, t, dark, card, btnP, btnG, btnSm, flash } = useApp();
  const [status, setStatus] = useState(null);
  const [properties, setProperties] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!activeId) return;
    apiFetch(`/api/projects/${activeId}/ga4/status`).then(r => r.json()).then(setStatus).catch(() => {});
    // Try to load existing snapshot if propertyId set
    apiFetch(`/api/projects/${activeId}/ga4/metrics?days=30`).then(async r => {
      if (r.ok) setMetrics(await r.json());
    }).catch(() => {});
  }, [activeId]);

  const reconnectGSC = async () => {
    try {
      const r = await apiFetch(`/api/gsc/auth-url?projectId=${activeId}`);
      const d = await r.json();
      if (d.url) window.open(d.url, "_blank", "width=600,height=700");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const disconnectGSC = async () => {
    if (!window.confirm("Відключити Google акаунт від цього проєкту? Після цього треба буде підключити заново — це згенерує новий refresh_token і запросить scope для GSC + Analytics.")) return;
    try {
      const r = await apiFetch(`/api/gsc/disconnect?projectId=${activeId}`, { method: "DELETE" });
      const d = await r.json();
      if (d.ok) {
        flash("✓ Google акаунт відключено");
        setStatus({ connected: false });
        setProperties(null);
        setMetrics(null);
      } else flash(d.error || "Помилка");
    } catch (e) { flash("Помилка: " + e.message); }
  };

  const listProperties = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ga4/properties`);
      const d = await r.json();
      if (d.properties) setProperties(d.properties);
      else flash(d.error || "Помилка отримання списку");
    } catch (e) { flash("Помилка: " + e.message); }
    setLoading(false);
  };

  const setProperty = async (propertyId) => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ga4/property`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ propertyId }),
      });
      const d = await r.json();
      if (d.ok) {
        flash(`Property збережено: ${d.propertyId}`);
        const s = await apiFetch(`/api/projects/${activeId}/ga4/status`).then(x => x.json());
        setStatus(s);
        setProperties(null);
        loadMetrics(days);
      } else flash(d.error);
    } catch (e) { flash("Помилка: " + e.message); }
    setLoading(false);
  };

  const loadMetrics = async (d) => {
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/ga4/metrics?days=${d || days}`);
      const data = await r.json();
      if (r.ok) setMetrics(data);
      else flash(data.error || "Помилка");
    } catch (e) { flash("Помилка: " + e.message); }
    setLoading(false);
  };

  if (!proj) return null;

  return (
    <div style={{ padding: 24, color: t.text, maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>📊 Google Analytics</h1>
        {status?.connected && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={reconnectGSC} title="Перепідключити заново — отримати свіжий refresh_token" style={{ ...btnG, fontSize: 12, height: 30, padding: "0 12px" }}>↻ Перепідключити</button>
            <button onClick={disconnectGSC} title="Видалити OAuth токени з цього проєкту" style={{ background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, padding: "0 12px", fontSize: 12, fontWeight: 600, height: 30, cursor: "pointer" }}>🔌 Відключити</button>
          </div>
        )}
      </div>
      <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 20 }}>
        GA4 Data API · {proj.domain} · {status?.propertyId ? `Property ${status.propertyId}` : "не підключено"}
      </div>

      {/* Status / Setup */}
      {!status?.connected && (
        <div style={{ ...card, marginBottom: 16, padding: 16, borderLeft: "4px solid #ef4444" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>GSC не підключено</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 10 }}>
            GA4 використовує той самий OAuth що і GSC. Підключіть Google акаунт з доступом до Analytics:
          </div>
          <button onClick={reconnectGSC} style={btnP}>Підключити Google</button>
        </div>
      )}

      {status?.connected && !status?.propertyId && (
        <div style={{ ...card, marginBottom: 16, padding: 16, borderLeft: "4px solid #f59e0b" }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>GSC підключено, оберіть GA4 property</div>
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 10 }}>
            Якщо переходили з GSC у старій версії — переподключіть для отримання scope `analytics.readonly`.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={listProperties} disabled={loading} style={btnP}>
              {loading ? "Завантаження..." : "Показати доступні properties"}
            </button>
            <button onClick={reconnectGSC} style={btnG}>↻ Переподключити Google (якщо немає доступу)</button>
          </div>

          {properties && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
              {properties.length === 0 && (
                <div style={{ fontSize: 12, color: t.textMuted }}>
                  Не знайдено GA4 properties. Перевірте, що ваш Google-акаунт має доступ до GA4 для цього сайту.
                </div>
              )}
              {properties.map((p) => (
                <div key={p.property} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: `1px solid ${t.border}`, borderRadius: 8, background: t.bgCard }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.displayName}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "monospace" }}>{p.property} · {p.account}</div>
                  </div>
                  <button onClick={() => setProperty(p.property)} disabled={loading} style={{ ...btnSm }}>Обрати</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {status?.connected && status?.propertyId && (
        <>
          {/* Range selector + refresh */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => { setDays(d); loadMetrics(d); }} style={{ ...btnSm, background: days === d ? "linear-gradient(135deg,#6366f1,#a855f7)" : undefined, color: days === d ? "#fff" : t.text }}>
                {d} днів
              </button>
            ))}
            <button onClick={() => loadMetrics(days)} disabled={loading} style={{ ...btnSm }}>{loading ? "..." : "↻ Оновити"}</button>
            <span style={{ flex: 1 }} />
            {metrics?.fetchedAt && <span style={{ fontSize: 11, color: t.textMuted }}>Оновлено: {new Date(metrics.fetchedAt).toLocaleString("uk-UA")}</span>}
          </div>

          {/* KPIs */}
          {metrics && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 18 }}>
                <KPI label="Сесії" value={metrics.cur?.sessions} delta={fmtPct(metrics.cur?.sessions, metrics.prev?.sessions)} color="#6366f1" t={t} dark={dark} />
                <KPI label="Користувачі" value={metrics.cur?.users} delta={fmtPct(metrics.cur?.users, metrics.prev?.users)} color="#a855f7" t={t} dark={dark} />
                <KPI label="Нові" value={metrics.cur?.newUsers} delta={fmtPct(metrics.cur?.newUsers, metrics.prev?.newUsers)} color="#3b82f6" t={t} dark={dark} />
                <KPI label="Engaged сесії" value={metrics.cur?.engagedSessions} delta={fmtPct(metrics.cur?.engagedSessions, metrics.prev?.engagedSessions)} color="#10b981" t={t} dark={dark} />
                <KPI label="Перегляди" value={metrics.cur?.pageViews} delta={fmtPct(metrics.cur?.pageViews, metrics.prev?.pageViews)} color="#0ea5e9" t={t} dark={dark} />
                <KPI label="Конверсії" value={metrics.cur?.conversions} delta={fmtPct(metrics.cur?.conversions, metrics.prev?.conversions)} color="#f59e0b" t={t} dark={dark} />
              </div>

              {/* Channels */}
              {metrics.sources?.length > 0 && (
                <div style={{ ...card, marginBottom: 14, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontWeight: 700 }}>Джерела трафіку</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead style={{ background: t.bg2, color: t.textMuted }}>
                      <tr><th style={{ textAlign: "left", padding: "8px 16px" }}>Канал</th><th style={{ textAlign: "right", padding: "8px 16px" }}>Сесії</th><th style={{ textAlign: "right", padding: "8px 16px" }}>Конверсії</th></tr>
                    </thead>
                    <tbody>
                      {metrics.sources.map((s, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 16px" }}>{s.channel}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", fontFamily: "monospace" }}>{fmtNum(s.sessions)}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", fontFamily: "monospace" }}>{fmtNum(s.conversions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Top landing pages */}
              {metrics.landingPages?.length > 0 && (
                <div style={{ ...card, marginBottom: 14, padding: 0, overflow: "hidden" }}>
                  <div style={{ padding: "10px 16px", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontWeight: 700 }}>Топ landing-сторінки</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead style={{ background: t.bg2, color: t.textMuted }}>
                      <tr><th style={{ textAlign: "left", padding: "8px 16px" }}>Сторінка</th><th style={{ textAlign: "right", padding: "8px 16px" }}>Сесії</th><th style={{ textAlign: "right", padding: "8px 16px" }}>Конверсії</th></tr>
                    </thead>
                    <tbody>
                      {metrics.landingPages.map((p, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${t.border}` }}>
                          <td style={{ padding: "8px 16px", fontFamily: "monospace", fontSize: 11, maxWidth: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.page}>{p.page}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", fontFamily: "monospace" }}>{fmtNum(p.sessions)}</td>
                          <td style={{ padding: "8px 16px", textAlign: "right", fontFamily: "monospace" }}>{fmtNum(p.conversions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Devices */}
              {metrics.devices?.length > 0 && (
                <div style={{ ...card, padding: "14px 18px" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Пристрої</div>
                  <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
                    {metrics.devices.map((d, i) => (
                      <div key={i}>
                        <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>{d.device}</div>
                        <div style={{ fontSize: 18, fontWeight: 600, fontFamily: "monospace" }}>{fmtNum(d.sessions)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: 16, fontSize: 11, color: t.textMuted }}>
            <button onClick={listProperties} style={{ ...btnSm }}>Змінити property</button>
          </div>

          {properties && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
              {properties.map((p) => (
                <div key={p.property} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", border: `1px solid ${t.border}`, borderRadius: 8, background: t.bgCard }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.displayName}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "monospace" }}>{p.property}</div>
                  </div>
                  <button onClick={() => setProperty(p.property)} style={{ ...btnSm }}>Обрати</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
